import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Seo } from "@/components/Seo";
import { Check, ArrowRight, GraduationCap, Calendar, Repeat, Sparkles, AlertCircle } from "lucide-react";
import { trackEvent } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GST_RATE = 0.1;

const TIERS = [
  {
    id: "pay-per-class",
    header: "Maximum flexibility",
    icon: Calendar,
    title: "Pay Per Class",
    subtitle: "Book and pay for individual classes as needed",
    price: "85",
    cadence: "per class",
    savings: null,
    features: [
      "Book any upcoming class",
      "Pay only when you attend",
      "No commitment, cancel anytime with 7 days' notice",
      "Comprehensive notes & weekly homework",
      "24/7 Ask Anything tutor chat",
    ],
    baseTotal: 85,
    cta: { label: "Buy 1 credit — $85 + GST", packageId: "single" },
    secondary: { label: "Or browse schedule", to: "/schedule" },
    highlighted: false,
  },
  {
    id: "weekly",
    header: "Save $5 per class",
    icon: Repeat,
    title: "Weekly Plan",
    subtitle: "1 class per week — card charged weekly",
    price: "80",
    cadence: "per week, auto-renewing",
    savings: "Save $5 per class",
    features: [
      "Consistent weekly attendance",
      "Card charged $88 every week ($80 + GST)",
      "+1 credit lands in your account each week",
      "Cancel anytime — credits keep working",
      "Comprehensive notes & weekly homework",
      "24/7 Ask Anything tutor chat",
    ],
    baseTotal: 80,
    weekly: true,
    cta: { label: "Start subscription — $80/week + GST", subscribe: true },
    secondary: { label: "Or sign up free", to: "/member/signup" },
    highlighted: false,
  },
  {
    id: "term-10",
    header: "Most popular",
    icon: GraduationCap,
    title: "10-Week Term Package",
    subtitle: "10 classes over one term",
    price: "75",
    pricePer: "per class",
    total: "$750 total + GST",
    cadence: "10 classes — best balance of savings & commitment",
    savings: "Save $100",
    features: [
      "10 credits added to your account",
      "Top-scoring exemplar essays & solutions",
      "Year-round access to lesson recordings",
      "24/7 Ask Anything tutor chat",
      "Progress tracking",
    ],
    baseTotal: 750,
    cta: { label: "Buy 10 credits — $750 + GST", packageId: "term-10" },
    secondary: { label: "Or sign up free", to: "/member/signup" },
    highlighted: true,
  },
  {
    id: "quarter-16",
    header: "Best value",
    icon: Sparkles,
    title: "16-Week Quarter Package",
    subtitle: "16 classes over 4 months",
    price: "70",
    pricePer: "per class",
    total: "$1,120 total + GST",
    cadence: "16 classes — for serious students",
    savings: "Save $240",
    features: [
      "16 credits added to your account",
      "All Term Package benefits included",
      "Practice exams under exam conditions",
      "Priority email & SMS support",
      "1-on-1 mentor workshops each term",
    ],
    baseTotal: 1120,
    cta: { label: "Buy 16 credits — $1,120 + GST", packageId: "quarter-16" },
    secondary: { label: "Or sign up free", to: "/member/signup" },
    highlighted: false,
  },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    // Auto-resume after signup/login if user clicked "Buy" while logged out
    const raw = sessionStorage.getItem("pricing_intent");
    const token = localStorage.getItem("member_token");
    if (raw && token) {
      sessionStorage.removeItem("pricing_intent");
      try {
        const intent = JSON.parse(raw);
        checkout(intent);
      } catch {
        checkout({ packageId: raw, subscribe: raw === "__subscribe__" });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkout = async ({ packageId, subscribe, subjectId }) => {
    const token = localStorage.getItem("member_token");
    setBuying(subscribe ? "__subscribe__" : packageId);
    try {
      if (subscribe) {
        trackEvent("begin_checkout", { content_type: "subscription", content_ids: ["weekly_subscription"] });
        const res = await axios.post(
          `${API}/member/checkout/subscribe-weekly`,
          { origin_url: window.location.origin, subject_id: subjectId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        window.location.href = res.data.url;
        return;
      }
      trackEvent("begin_checkout", { content_type: "credit_pack", content_ids: [packageId] });
      const res = await axios.post(
        `${API}/member/checkout/credits`,
        { package_id: packageId, origin_url: window.location.origin, subject_id: subjectId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.url;
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("member_token");
        sessionStorage.setItem("pricing_intent", JSON.stringify({ packageId, subscribe, subjectId }));
        toast.info("Please log in to continue.");
        navigate("/member/login");
      } else {
        toast.error(err.response?.data?.detail || "Could not start checkout");
      }
      setBuying(null);
    }
  };

  const handleBuy = async (packageId, opts = {}) => {
    const token = localStorage.getItem("member_token");
    if (!token) {
      sessionStorage.setItem("pricing_intent", JSON.stringify({ packageId, subscribe: !!opts.subscribe }));
      toast.info("Sign in or create your free account to purchase.");
      navigate("/member/signup");
      return;
    }
    checkout({ packageId, subscribe: !!opts.subscribe });
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0f1117]" data-testid="pricing-page">
      <Seo
        title="Pricing — Small group English classes from $70 + GST per class"
        description="Pricing for Tutors Melbourne small group English classes: $85 pay-per-class, $80/week weekly plan, $750 for a 10-week term or $1,120 for a 16-week package. All prices exclude GST — 10% is added at checkout."
        path="/pricing"
      />
      <Navbar />

      <section className="hero-gradient dark:bg-transparent pt-28 pb-12 px-6">
        <div className="max-w-3xl mx-auto text-center animate-fade-in-up">
          <Badge className="bg-brand-purple-soft text-brand-purple mb-4 font-body font-semibold px-4 py-1.5 text-sm rounded-full">Simple, honest pricing</Badge>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-white tracking-tight mb-6">
            Choose the plan that fits your goals
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 font-body leading-relaxed">
            All prices <strong>exclude GST</strong> — 10% is added at checkout. Pay per class, or buy a package and
            save up to <strong>$240</strong>. Every plan gets you small classes, qualified VCE tutors and premium resources.
          </p>
        </div>
      </section>

      <section className="py-10 px-6 bg-white dark:bg-[#0f1117]">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6" data-testid="pricing-grid">
          {TIERS.map((t) => <PricingCard key={t.id} tier={t} onBuy={handleBuy} buying={buying} />)}
        </div>
      </section>

      {/* Cancellation + No-show policy */}
      <section className="py-12 px-6 bg-slate-50 dark:bg-[#0a0b10]" data-testid="policies-section">
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-[#1a1b23] rounded-2xl border border-slate-100 dark:border-slate-800 p-6">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="h-5 w-5 text-brand-purple dark:text-brand-purple-light" />
              <h3 className="font-heading text-lg font-bold text-slate-900 dark:text-white">Cancellation & Refund Policy</h3>
            </div>
            <ul className="space-y-3 text-sm text-slate-600 dark:text-slate-300 font-body">
              <li>
                <strong className="text-slate-900 dark:text-white">Pay Per Class / Weekly Plan:</strong> Cancel anytime with 7 days' notice. You won't be charged for future weeks.
              </li>
              <li>
                <strong className="text-slate-900 dark:text-white">10-Week Term Package:</strong> Cancel with 14 days' notice. You'll be charged for classes already attended plus a $50 admin fee (or 1 class equivalent). Remaining balance refunded.
              </li>
              <li>
                <strong className="text-slate-900 dark:text-white">16-Week Quarter Package:</strong> Same terms as above with a $75 admin fee.
              </li>
            </ul>
          </div>

          <div className="bg-white dark:bg-[#1a1b23] rounded-2xl border border-slate-100 dark:border-slate-800 p-6">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="h-5 w-5 text-brand-gold" />
              <h3 className="font-heading text-lg font-bold text-slate-900 dark:text-white">No-Show Policy</h3>
            </div>
            <ul className="space-y-3 text-sm text-slate-600 dark:text-slate-300 font-body">
              <li>Students are charged for missed classes unless 24 hours' notice is given.</li>
              <li>You get <strong className="text-slate-900 dark:text-white">2 free no-shows per term</strong>, then the full class charge applies.</li>
              <li>Credits added to your account expire after <strong className="text-slate-900 dark:text-white">10 weeks of no usage</strong>. Book at least one class within that window to keep your balance active.</li>
            </ul>
          </div>
        </div>
        <p className="text-center text-xs text-slate-400 dark:text-slate-500 font-body mt-8 max-w-2xl mx-auto">
          All prices in AUD and exclude GST — 10% GST is added at checkout. Have a question about a plan? <Link to="/contact" className="text-brand-purple hover:underline">Contact us</Link> — we'll match you to the right one.
        </p>
      </section>

      <section className="py-16 px-6 bg-white dark:bg-[#0f1117]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">Not sure where to start?</h2>
          <p className="text-slate-600 dark:text-slate-400 font-body text-lg mb-8">
            Create a free account in seconds, then buy credits for the subject you need. Prefer to talk it through first?
            Send us a message and we'll match you to the right plan.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/member/signup" data-testid="pricing-bottom-cta">
              <Button className="bg-brand-gold hover:bg-brand-gold-hover text-white rounded-full px-10 py-6 font-bold font-body">
                Create free account <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/enrol" data-testid="pricing-bottom-cta-trial">
              <Button variant="outline" className="rounded-full px-10 py-6 font-bold font-body border-brand-purple text-brand-purple hover:bg-brand-purple-soft">
                Enrol for next term
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="bg-slate-50 dark:bg-[#0a0b10] border-t border-slate-100 dark:border-slate-800 py-12 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="font-heading text-lg font-bold text-brand-purple dark:text-brand-purple-light">Tutors Melbourne</p>
          <p className="text-sm text-brand-gold font-body font-semibold">Learning Perfected</p>
        </div>
      </footer>

    </div>
  );
}

function PricingCard({ tier, onBuy, buying }) {
  const Icon = tier.icon;
  const isSubscribe = !!tier.cta.subscribe;
  const buyingKey = isSubscribe ? "__subscribe__" : tier.cta.packageId;
  const isBuying = buying === buyingKey;
  return (
    <div className={`rounded-3xl overflow-hidden border-2 flex flex-col transition-all hover:-translate-y-1 ${
      tier.highlighted
        ? "border-brand-gold shadow-[0_20px_50px_rgba(229,161,0,0.2)] dark:shadow-[0_20px_50px_rgba(229,161,0,0.15)] scale-[1.02]"
        : "border-slate-100 dark:border-slate-800 hover:border-brand-purple/40"
    }`} data-testid={`pricing-tier-${tier.id}`}>
      <div className={`text-center py-3 ${tier.highlighted ? "bg-brand-gold" : "bg-brand-purple"}`}>
        <span className="text-xs font-body font-semibold uppercase tracking-wider text-white">{tier.header}</span>
      </div>

      <div className="bg-white dark:bg-[#1a1b23] p-7 flex-1 flex flex-col">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${
          tier.highlighted ? "bg-brand-gold/10" : "bg-brand-purple-soft dark:bg-brand-purple/20"
        }`}>
          <Icon className={`h-6 w-6 ${tier.highlighted ? "text-brand-gold" : "text-brand-purple dark:text-brand-purple-light"}`} />
        </div>

        <h3 className="font-heading text-xl font-bold text-slate-900 dark:text-white mb-1">{tier.title}</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 font-body mb-5 min-h-[40px]">{tier.subtitle}</p>

        <div className="mb-5">
          <div className="flex items-baseline gap-1">
            <span className="font-heading text-4xl font-bold text-slate-900 dark:text-white">${tier.price}</span>
            <span className="text-sm text-slate-500 dark:text-slate-400 font-body font-semibold">+ GST</span>
            {tier.pricePer && <span className="text-xs text-slate-400 dark:text-slate-500 font-body">{tier.pricePer}</span>}
          </div>
          {tier.total && <p className="text-sm text-brand-purple dark:text-brand-purple-light font-body font-semibold mt-1">{tier.total}</p>}
          <p className="text-xs text-slate-400 dark:text-slate-500 font-body mt-1">{tier.cadence}</p>
          <p className="text-xs text-slate-400 dark:text-slate-500 font-body mt-1" data-testid={`tier-${tier.id}-gst-total`}>
            ${(tier.baseTotal * (1 + GST_RATE)).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} charged{tier.weekly ? " weekly" : ""} incl. GST
          </p>
          {tier.savings && (
            <Badge className="mt-2 bg-green-50 text-green-700 border border-green-200 font-body text-xs rounded-full">{tier.savings}</Badge>
          )}
        </div>

        <ul className="space-y-2.5 mb-6 flex-1">
          {tier.features.map((f, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300 font-body" data-testid={`tier-${tier.id}-feature-${i}`}>
              <Check className={`h-4 w-4 flex-shrink-0 mt-0.5 ${tier.highlighted ? "text-brand-gold" : "text-brand-purple dark:text-brand-purple-light"}`} />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        <div className="space-y-2 mt-auto">
          {(tier.cta.packageId || tier.cta.subscribe) ? (
            <Button
              onClick={() => onBuy(tier.cta.packageId, { subscribe: !!tier.cta.subscribe })}
              disabled={buying !== null}
              className={`w-full rounded-full font-body font-bold py-5 ${
                tier.highlighted
                  ? "bg-brand-gold hover:bg-brand-gold-hover text-white"
                  : "bg-brand-purple hover:bg-brand-purple-hover text-white"
              }`}
              data-testid={`tier-${tier.id}-cta`}
            >
              {isBuying ? "Redirecting…" : tier.cta.label}
            </Button>
          ) : (
            <Link to={tier.cta.to} className="block" data-testid={`tier-${tier.id}-cta`}>
              <Button className={`w-full rounded-full font-body font-bold py-5 ${
                tier.highlighted
                  ? "bg-brand-gold hover:bg-brand-gold-hover text-white"
                  : "bg-brand-purple hover:bg-brand-purple-hover text-white"
              }`}>
                {tier.cta.label}
              </Button>
            </Link>
          )}
          <Link to={tier.secondary.to} className="block" data-testid={`tier-${tier.id}-cta-secondary`}>
            <Button variant="outline" className="w-full rounded-full font-body font-semibold py-5 border-slate-300 dark:border-slate-700 hover:border-brand-purple hover:text-brand-purple text-xs">
              {tier.secondary.label}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
